export type LangCode =
  | 'en' | 'ru' | 'de' | 'fr' | 'es' | 'pt'
  | 'pl' | 'nl' | 'tr' | 'it' | 'cs' | 'ro'
  | 'uk' | 'ar' | 'zh' | 'ja' | 'ko';

export interface TranslateOptions {
  from?: LangCode | string;
  to: LangCode | string;
  timeout?: number;
}

export interface TranslateResult {
  text: string;
  translated: boolean;
}

const GOOGLE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';

export async function translateText(
  text: string,
  options: TranslateOptions,
): Promise<TranslateResult> {
  const from    = options.from ?? 'en';
  const to      = options.to;
  const timeout = options.timeout ?? 10_000;

  if (!text.trim() || from === to) {
    return { text, translated: true };
  }

  const url = new URL(GOOGLE_ENDPOINT);
  url.searchParams.set('client', 'gtx');
  url.searchParams.set('sl', from);
  url.searchParams.set('tl', to);
  url.searchParams.set('dt', 't');
  url.searchParams.set('q', text);

  const controller = new AbortController();
  const timer      = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: [[[string, string, ...unknown[]], ...unknown[]], ...unknown[]] =
      await response.json();

    const translated = data[0]
      .map((segment: unknown) => (Array.isArray(segment) ? segment[0] : ''))
      .filter(Boolean)
      .join('');

    return { text: translated || text, translated: Boolean(translated) };
  } catch (error: unknown) {
    const reason = error instanceof DOMException && error.name === 'AbortError'
      ? 'таймаут'
      : error instanceof Error
        ? error.message
        : String(error);
    console.warn(`[translation] ${from}→${to} failed (${reason}), using original`);
    return { text, translated: false };
  } finally {
    clearTimeout(timer);
  }
}

export interface BatchItem {
  key: string;
  text: string;
}

export interface BatchOptions extends Omit<TranslateOptions, 'to'> {
  to: LangCode | string;
  delay?: number;
  onProgress?: (completed: number, total: number, key: string) => void;
}

export async function translateBatch(
  items: BatchItem[],
  options: BatchOptions,
  stopSignal?: { current: boolean },
): Promise<Record<string, string>> {
  const delay  = options.delay ?? 200;
  const result: Record<string, string> = {};

  for (let i = 0; i < items.length; i++) {
    if (stopSignal?.current) break;

    const { key, text } = items[i];
    const { text: translated } = await translateText(text, options);
    result[key] = translated;

    options.onProgress?.(i + 1, items.length, key);

    if (delay > 0 && i < items.length - 1) {
      await new Promise<void>(r => setTimeout(r, delay));
    }
  }

  return result;
}

export interface LanguageInfo {
  code: string;
  abbr: string;
  name: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', abbr: 'EN', name: 'English' },
  { code: 'it', abbr: 'IT', name: 'Italian' },
  { code: 'de', abbr: 'DE', name: 'German' },
  { code: 'fr', abbr: 'FR', name: 'French' },
  { code: 'es', abbr: 'ES', name: 'Spanish' },
  { code: 'pt', abbr: 'PT', name: 'Portuguese' },
  { code: 'pl', abbr: 'PL', name: 'Polish' },
  { code: 'nl', abbr: 'NL', name: 'Dutch' },
  { code: 'tr', abbr: 'TR', name: 'Turkish' },
  { code: 'ru', abbr: 'RU', name: 'Russian' },
  { code: 'cs', abbr: 'CS', name: 'Czech' },
  { code: 'ro', abbr: 'RO', name: 'Romanian' },
];